import {
  useEffect,
  useMemo,
  useRef,
  useState,
  ChangeEvent,
  KeyboardEvent,
  ComponentProps,
} from 'react';
import { LucideIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface InputDropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface InputDropdownProps
  extends Omit<ComponentProps<'input'>, 'onChange' | 'onSelect'> {
  error?: string;
  icon?: LucideIcon;
  options: InputDropdownOption[];
  onSelect?: (option: InputDropdownOption) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  maxHeight?: string;
}

function InputDropdown({
  className,
  type = 'text',
  icon: Icon,
  options,
  onSelect,
  onChange,
  placeholder,
  maxHeight = 'max-h-96',
  error,
  value,
  ...props
}: InputDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState((value as string) || '');
  const [selectedOption, setSelectedOption] =
    useState<InputDropdownOption | null>(null);

  const defaultValue = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  useEffect(() => {
    if (defaultValue && selectedOption?.value !== defaultValue.value) {
      setSelectedOption(defaultValue);
      setInputValue(defaultValue.label);
    }
  }, [defaultValue, selectedOption?.value]);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update input value when controlled value changes
  useEffect(() => {
    if (value !== undefined) {
      // If there's a matching option, use its label; otherwise use the raw value
      const matchingOption = options.find((option) => option.value === value);
      setInputValue(matchingOption ? matchingOption.label : (value as string));
    }
  }, [value, options]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedOption(null);
    onChange?.(newValue);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleOptionSelect = (option: InputDropdownOption) => {
    if (option.disabled) return;

    setInputValue(option.label);
    setSelectedOption(option);
    setIsOpen(false);
    onSelect?.(option);
    onChange?.(option.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      // Keep the typed value instead of selecting first option
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      inputRef.current?.focus();
    }
  };

  return (
    <div className={cn('relative', className)}>
      <div className='relative w-full'>
        <input
          ref={inputRef}
          type={type}
          data-slot='input'
          className={cn(
            'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive w-full',
            {
              'border-rose-600 dark:border-rose-400': error,
              'pr-16': Icon,
              'pr-9': !Icon,
            },
            className,
          )}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          role='combobox'
          {...props}
        />

        <div className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-auto'>
          {Icon && <Icon className='h-4 w-4 text-muted-foreground' />}
          <button
            type='button'
            onClick={props.disabled ? undefined : toggleDropdown}
            className={cn(
              'text-muted-foreground transition-colors',
              !props.disabled && 'hover:text-foreground cursor-pointer',
              props.disabled && 'cursor-not-allowed opacity-50',
            )}
            tabIndex={-1}
            aria-disabled={props.disabled}
          >
            <ChevronDownIcon
              className={cn(
                'h-4 w-4 transition-transform',
                isOpen && 'rotate-180',
              )}
            />
          </button>
        </div>
      </div>

      {error && (
        <span className='text-xs text-rose-600 pl-[13px] dark:text-rose-400 break-words'>
          {error}
        </span>
      )}

      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(
            'absolute z-50 w-full mt-1 bg-popover text-popover-foreground rounded-md border shadow-md animate-in fade-in-0 zoom-in-95',
            maxHeight,
            'overflow-auto',
          )}
        >
          <div className='p-1'>
            {options.length === 0 ? (
              <div className='px-2 py-1.5 text-sm text-muted-foreground'>
                No options available
              </div>
            ) : (
              options.map((option, index) => (
                <div
                  key={`${option.value}-${index}`}
                  className={cn(
                    'relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus:bg-accent focus:text-accent-foreground',
                    option.disabled && 'pointer-events-none opacity-50',
                  )}
                  onClick={() => handleOptionSelect(option)}
                  role='option'
                  aria-selected={selectedOption?.value === option.value}
                >
                  <span className='flex items-center gap-2 flex-1'>
                    {option.label}
                  </span>
                  {selectedOption?.value === option.value && (
                    <span className='absolute right-2 flex size-3.5 items-center justify-center'>
                      <CheckIcon className='size-4' />
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { InputDropdown };
